import DOMPurify from "dompurify";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { CollapsibleStep } from "../../../../Shared/Collapsible/React";
import { useProvetContext, useTranslationContext } from "../../context";
import { ProvetBookingFlowActionType } from "../../hooks/provetBookingFlowReducer";
import { ProvetAppointmentTimePicker } from "../../modules/provetAppointmentTimePicker";
import { ProvetContactDetails } from "../../modules/provetContactDetails";
import { ProvetDepartmentFinder } from "../../modules/provetDepartmentFinder";
import {
  ProvetPhoneVerification,
  VerificationStep
} from "../../modules/provetPhoneVerification/provetPhoneVerification";
import { ProvetTreatmentFinder } from "../../modules/provetTreatmentFinder";
import {
  ContactDetailsEventArgs,
  ContactDetailsSubStep,
  FormSelectItem,
  ProvetDepartment,
  ProvetDialogStep,
  Reason,
  SelectedAppointmentTime,
  Species,
  getContactDetailsSubStep
} from "../../types";
import { AppointmentTimeDisplay } from "./appointmentTimeDisplay";

interface ProvetFormStepsProps {
  sessionId: string;
  formResetCounter: number;
  treatmentNotes?: string;
  defaultSpecies?: FormSelectItem[];
  oldBookingDomain?: string;
  mapboxAccessToken?: string;
  preludeSdkKey?: string;
  activeProvetDepartmentIds: number[];
  excludedProvetDepartmentIds: number[];
  defaultLatitude: number;
  defaultLongitude: number;
  defaultZoom: number;
  onSelectDepartment: (
    department: ProvetDepartment,
    transition?: boolean
  ) => Promise<void>;
  onSelectReason: (reason: Reason) => void;
  onSelectAppointmentTime: (appointmentTime?: SelectedAppointmentTime) => Promise<void>;
  onContactDetailsChanged: (contactDetails: ContactDetailsEventArgs) => Promise<void>;
  onValidationError: () => void;
  onEditStep: (
    step: ProvetDialogStep,
    isComplete?: boolean,
    transition?: boolean
  ) => Promise<void>;
  onContactDetailsSubStepChange: (subStep: ContactDetailsSubStep) => Promise<void>;
  preselectedClinic?: ProvetDepartment;
  preselectedTreatment?: Reason;
  /** A linked treatment is waiting for the treatments to load */
  isTreatmentPreselectionPending?: boolean;
  clearPreselectedClinic?: () => void;
}

/**
 * Renders all booking form steps in collapsible sections.
 *
 * This component manages the four main steps of the booking process, displaying
 * them as collapsible sections that expand/collapse based on the current booking flow state.
 * Each step includes validation, preselection support, and edit capabilities.
 *
 * **Booking Steps:**
 * 1. **Select Clinic** - Choose veterinary clinic with map/list view
 * 2. **Select Treatment** - Choose reason for visit from available treatments
 * 3. **Select Date & Time** - Pick appointment slot with available veterinarian
 * 4. **Contact Details** - Enter customer and pet information
 *
 * **Key Features:**
 * - Collapsible step UI with completion indicators
 * - URL parameter preselection support (clinic and treatment)
 * - Edit functionality to return to previous steps
 * - Automatic form data clearing when editing earlier steps
 * - Treatment notes display (from selected clinic or global config)
 * - Form reset handling via reset counter
 *
 * **Step State Management:**
 * - Steps are open when active and incomplete
 * - Completed steps show summary text and collapse
 * - Preselected values show confirmation UI
 * - Editing earlier steps clears dependent data
 *
 * @param sessionId - Unique session identifier for element IDs
 * @param formResetCounter - Counter to force form reset (key prop)
 * @param treatmentNotes - Optional HTML notes about treatments (sanitized with DOMPurify)
 * @param defaultSpecies - Pre-populated species list for contact form
 * @param oldBookingDomain - Legacy booking domain for fallback links
 * @param mapboxAccessToken - Mapbox access token for clinic map
 * @param activeProvetDepartmentIds - IDs of departments available for booking
 * @param excludedProvetDepartmentIds - IDs of departments to hide from list
 * @param defaultLatitude - Default map center latitude
 * @param defaultLongitude - Default map center longitude
 * @param defaultZoom - Default map zoom level
 * @param onSelectDepartment - Handler for clinic selection
 * @param onSelectReason - Handler for treatment selection
 * @param onSelectAppointmentTime - Handler for time slot selection
 * @param onContactDetailsChanged - Handler for contact form submission
 * @param onValidationError - Handler for form validation errors
 * @param onEditStep - Handler for returning to previous steps
 * @param preselectedClinic - Optional pre-selected clinic from URL params
 * @param preselectedTreatment - Optional pre-selected treatment from URL params
 * @param clearPreselectedClinic - Callback to clear pre-selected clinic
 *
 * @example
 * ```tsx
 * <ProvetFormSteps
 *   sessionId="booking-abc123"
 *   formResetCounter={0}
 *   defaultLatitude={59.3293}
 *   defaultLongitude={18.0686}
 *   defaultZoom={8}
 *   mapboxAccessToken="your-access-token"
 *   activeProvetDepartmentIds={[1, 2, 3]}
 *   excludedProvetDepartmentIds={[]}
 *   onSelectDepartment={handleDepartmentSelection}
 *   onSelectReason={handleReasonSelection}
 *   onSelectAppointmentTime={handleTimeSelection}
 *   onContactDetailsChanged={handleContactDetails}
 *   onValidationError={scrollToError}
 *   onEditStep={handleEditStep}
 * />
 * ```
 */
export const ProvetFormSteps = ({
  sessionId,
  formResetCounter,
  treatmentNotes,
  defaultSpecies,
  oldBookingDomain,
  mapboxAccessToken,
  preludeSdkKey,
  activeProvetDepartmentIds,
  excludedProvetDepartmentIds,
  defaultLatitude,
  defaultLongitude,
  defaultZoom,
  onSelectDepartment,
  onSelectReason,
  onSelectAppointmentTime,
  onContactDetailsChanged,
  onValidationError,
  onEditStep,
  onContactDetailsSubStepChange,
  preselectedClinic,
  preselectedTreatment,
  isTreatmentPreselectionPending,
  clearPreselectedClinic
}: ProvetFormStepsProps) => {
  const provetContext = useProvetContext();
  const translationContext = useTranslationContext();
  const translations = translationContext?.translations;

  // Track internal phone verification step for conditional title rendering
  const [phoneVerificationStep, setPhoneVerificationStep] =
    useState<VerificationStep>("input");

  const contactDetailsSubStep = getContactDetailsSubStep(
    provetContext.bookingFlowState.completedSteps,
    phoneVerificationStep === "verify" || phoneVerificationStep === "loading"
  );

  // The phone verification starts at the email and phone input, and moves to the SMS code
  // input once the code has been sent (except for existing users, who skip that). The user can
  // go back from the code input to change the phone number.
  // (Only while on this step: the phone verification is also reset, and reports "input", when a
  // new booking is started.)
  const activeStepRef = useRef(provetContext.bookingFlowState.activeStep);
  activeStepRef.current = provetContext.bookingFlowState.activeStep;
  const handlePhoneVerificationStepChange = useCallback(
    (step: VerificationStep) => {
      setPhoneVerificationStep(step);

      if (step === "verify" || step === "loading") {
        onContactDetailsSubStepChange("verifySms");
      } else if (
        step === "input" &&
        activeStepRef.current === ProvetDialogStep.ContactDetails
      ) {
        onContactDetailsSubStepChange("emailAndPhone");
      }
    },
    [onContactDetailsSubStepChange]
  );
  // Track phone verification loading state
  const [isPhoneVerificationLoading, setIsPhoneVerificationLoading] = useState(false);

  // Determine if the clinic is preselected but not yet confirmed
  const isClinicPreselected =
    !!preselectedClinic && !provetContext.bookingFlowState.formData?.selectedDepartment;
  // Determine if the treatment is preselected but not yet confirmed. (It is only preselected
  // once a clinic it belongs to has been chosen, see useProvetPreselection.)
  // After Change, the list is shown instead (until the clinic is changed): the linked treatment
  // is only forgotten if a different one is chosen, see useProvetPreselection.
  const [isLinkedTreatmentDeferred, setIsLinkedTreatmentDeferred] = useState(false);
  useEffect(() => {
    // A new booking offers the linked treatment again
    setIsLinkedTreatmentDeferred(false);
  }, [formResetCounter]);
  const isTreatmentPreselected =
    !!preselectedTreatment &&
    !provetContext.bookingFlowState.formData?.selectedReason &&
    !isLinkedTreatmentDeferred;
  // The time chosen in Step 3. Step 3 only shows as complete with one, so it never shows a
  // tick without a time (eg after the clinic has been changed).
  const selectedAppointmentTime =
    provetContext.bookingFlowState.formData?.selectedAppointmentTime;

  // Handler for confirming a preselected clinic
  const handleConfirmClinic = useCallback(async () => {
    if (preselectedClinic) {
      await onSelectDepartment(preselectedClinic, true);
    }
  }, [preselectedClinic, onSelectDepartment]);

  // Handler for confirming a preselected treatment
  const handleConfirmTreatment = useCallback(async () => {
    if (preselectedTreatment) {
      onSelectReason(preselectedTreatment);
    }
  }, [preselectedTreatment, onSelectReason]);

  // Handler for changing clinic: clear preselected and form data. The treatment from the link
  // is kept, and offered again by any clinic that offers it.
  const handleChangeClinic = useCallback(async () => {
    clearPreselectedClinic?.();
    setIsLinkedTreatmentDeferred(false);
    // Clear selectedDepartment, selectedReason, and selectedAppointmentTime
    // We must spread formData to preserve sessionId, contactDetails, and reCaptchaResponse
    provetContext.bookingFlowDispatch({
      type: ProvetBookingFlowActionType.SetFormData,
      formData: {
        ...provetContext.bookingFlowState.formData,
        selectedDepartment: undefined,
        selectedReason: undefined,
        selectedAppointmentTime: undefined
      }
    });
    onEditStep(ProvetDialogStep.SelectClinic, false, true);
  }, [
    clearPreselectedClinic,
    provetContext.bookingFlowDispatch,
    provetContext.bookingFlowState.formData,
    onEditStep
  ]);

  // Handler for changing treatment: show the list instead of the linked treatment (which stays
  // remembered) and clear the form data
  const handleChangeTreatment = useCallback(async () => {
    setIsLinkedTreatmentDeferred(true);
    // Clear selectedReason and selectedAppointmentTime
    // Keep contactDetails so we can check species compatibility when selecting new treatment
    // If new treatment is incompatible with current species, setReason() will handle clearing it
    // We must spread formData to preserve sessionId, contactDetails, and reCaptchaResponse
    provetContext.bookingFlowDispatch({
      type: ProvetBookingFlowActionType.SetFormData,
      formData: {
        ...provetContext.bookingFlowState.formData,
        selectedReason: undefined,
        selectedAppointmentTime: undefined
      }
    });
    // Mark contact details as incomplete since species might need to be revalidated
    provetContext.bookingFlowDispatch({
      type: ProvetBookingFlowActionType.SetCompletedSteps,
      completedSteps: {
        selectTreatment: false,
        selectDateAndTime: false,
        selectContactDetails: false
      }
    });
    // Reset species incompatibility warning
    provetContext.bookingFlowDispatch({
      type: ProvetBookingFlowActionType.SetSpeciesIncompatibleWarning,
      show: false
    });
    onEditStep(ProvetDialogStep.SelectTreatment, false, true);
  }, [
    provetContext.bookingFlowDispatch,
    provetContext.bookingFlowState.formData,
    onEditStep
  ]);

  // Handler for species selection in contact details - updates partial contact details for compatibility checking
  const handleSpeciesSelected = useCallback(
    (species: Species | undefined) => {
      // Update contact details with just the species for compatibility checking
      // This allows us to check species compatibility even before the form is fully submitted
      const currentContactDetails =
        provetContext.bookingFlowState.formData?.contactDetails;

      if (species) {
        provetContext.bookingFlowDispatch({
          type: ProvetBookingFlowActionType.SetFormData,
          formData: {
            contactDetails: {
              client: currentContactDetails?.client || {
                firstName: "",
                lastName: "",
                email: "",
                phoneNumber: "",
                streetAddress: "",
                zipCode: "",
                city: "",
                identificationNumber: "",
                extraField: ""
              },
              patient: {
                ...currentContactDetails?.patient,
                species: species,
                name: currentContactDetails?.patient?.name || ""
              }
            }
          }
        });
      }
    },
    [provetContext.bookingFlowDispatch, provetContext.bookingFlowState.formData]
  );

  // Handler for phone verification completion
  const handlePhoneVerified = useCallback(
    async (requestId: string) => {
      // The phone verification is unmounted now. If it has to be done again (eg when the
      // verification has expired by the time of booking), it starts at the email and phone.
      setPhoneVerificationStep("input");

      // Email and phone are already in contactDetails.client (updated by the component)
      // Just set the verification flags
      provetContext.bookingFlowDispatch({
        type: ProvetBookingFlowActionType.SetFormData,
        formData: {
          phoneVerified: true,
          verificationRequestId: requestId
        }
      });

      // Mark step as complete
      provetContext.bookingFlowDispatch({
        type: ProvetBookingFlowActionType.SetCompletedSteps,
        completedSteps: {
          verifyPhone: true
        }
      });

      // Ensure state updates have been applied
      await new Promise(resolve => setTimeout(resolve, 200));

      // Move to the last sub-step of ContactDetails, the rest of the contact details
      await onContactDetailsSubStepChange("details");

      // Move to next step (ContactDetails)
      await onEditStep(ProvetDialogStep.ContactDetails, true, true);
    },
    [provetContext.bookingFlowDispatch, onEditStep, onContactDetailsSubStepChange]
  );

  return (
    <>
      {/* Step 1: Select Clinic */}
      <CollapsibleStep
        step={ProvetDialogStep.SelectClinic}
        isComplete={!!provetContext.bookingFlowState.formData?.selectedDepartment}
        isPreselected={isClinicPreselected}
        stepCompleteMessageText={
          isClinicPreselected
            ? preselectedClinic?.name
            : provetContext?.bookingFlowState.formData?.selectedDepartment?.name
        }
        isOpen={
          provetContext.bookingFlowState.activeStep === ProvetDialogStep.SelectClinic &&
          !provetContext.bookingFlowState.formData?.selectedDepartment &&
          !isClinicPreselected
        }
        title={translations?.dialogClinicTitle}
        subtitle={translations?.dialogClinicSubtitle}
        onEdit={handleChangeClinic}
        onConfirm={handleConfirmClinic}
        id={`${sessionId}_${ProvetDialogStep.SelectClinic}`}
        editButtonText={translations?.change}
        confirmButtonText={translations?.confirm}
        changeButtonText={translations?.change}
        selectedValue={provetContext.bookingFlowState.formData?.selectedDepartment?.name}
      >
        <ProvetDepartmentFinder
          key={`department-${formResetCounter}`}
          onSelectDepartment={onSelectDepartment}
          defaultLatitude={defaultLatitude}
          defaultLongitude={defaultLongitude}
          defaultZoom={defaultZoom}
          oldBookingDomain={oldBookingDomain}
          mapboxAccessToken={mapboxAccessToken}
          activeProvetDepartmentIds={activeProvetDepartmentIds}
          excludedProvetDepartmentIds={excludedProvetDepartmentIds}
        />
      </CollapsibleStep>

      {/* Step 2: Select Treatment */}
      <CollapsibleStep
        step={ProvetDialogStep.SelectTreatment}
        isComplete={!!provetContext.bookingFlowState.formData?.selectedReason}
        isPreselected={isTreatmentPreselected}
        stepCompleteMessageText={
          isTreatmentPreselected
            ? preselectedTreatment?.name
            : provetContext?.bookingFlowState.formData?.selectedReason?.name
        }
        isOpen={
          provetContext.bookingFlowState.activeStep ===
            ProvetDialogStep.SelectTreatment &&
          !provetContext.bookingFlowState.formData?.selectedReason &&
          !isTreatmentPreselected
        }
        title={translations?.dialogTreatmentTitle}
        subtitle={translations?.dialogTreatmentSubtitle}
        onEdit={handleChangeTreatment}
        onConfirm={handleConfirmTreatment}
        id={`${sessionId}_${ProvetDialogStep.SelectTreatment}`}
        editButtonText={translations?.changeTreatment}
        confirmButtonText={translations?.confirm}
        changeButtonText={translations?.change}
        selectedValue={provetContext.bookingFlowState.formData?.selectedReason?.name}
      >
        {/* Treatment Notes */}
        {(treatmentNotes?.trim() ||
          provetContext.bookingFlowState.formData?.selectedDepartment?.online_booking_information_text?.trim()) && (
          <div className="p-md mb-md border border-neutral-100 rounded-tl-br-sm bg-neutral-100 text-t-neutral-800">
            <span className="flex mb-xs sm:mb-md items-center font-guardian text-t-md">
              {translations?.note}
            </span>
            <div className="text-t-sm font-normal">
              {provetContext.bookingFlowState.formData?.selectedDepartment?.online_booking_information_text?.trim() ? (
                provetContext.bookingFlowState.formData.selectedDepartment
                  .online_booking_information_text
              ) : (
                <div
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(treatmentNotes || "")
                  }}
                />
              )}
            </div>
          </div>
        )}

        <ProvetTreatmentFinder
          onSelectReason={onSelectReason}
          isPreselectionPending={isTreatmentPreselectionPending}
        />
      </CollapsibleStep>

      {/* Step 3: Select Date and Time */}
      <CollapsibleStep
        step={ProvetDialogStep.SelectDateAndTime}
        isComplete={
          provetContext.bookingFlowState.completedSteps.selectDateAndTime &&
          !!selectedAppointmentTime?.availableTime
        }
        stepCompleteMessageText={
          selectedAppointmentTime?.availableTime ? (
            <AppointmentTimeDisplay
              availableTime={selectedAppointmentTime.availableTime}
              selectedVeterinarianName={selectedAppointmentTime.selectedVeterinarianName}
              language={provetContext.language}
            />
          ) : undefined
        }
        isOpen={
          provetContext.bookingFlowState.activeStep === ProvetDialogStep.SelectDateAndTime
        }
        title={translations?.dialogDateAndTimeTitle}
        subtitle={translations?.dialogDateAndTimeSubtitle}
        onEdit={() => onEditStep(ProvetDialogStep.SelectDateAndTime)}
        id={`${sessionId}_${ProvetDialogStep.SelectDateAndTime}`}
        editButtonText={translations?.changeTime}
        selectedValue={
          selectedAppointmentTime?.availableTime
            ? [
                selectedAppointmentTime.availableTime,
                selectedAppointmentTime.selectedVeterinarianName
              ]
                .filter(Boolean)
                .join(" - ")
            : undefined
        }
      >
        <ProvetAppointmentTimePicker onSelectAppointmentTime={onSelectAppointmentTime} />
      </CollapsibleStep>

      {/* Step 4: Verify Phone & Contact Details - Hidden once both completed */}
      {!provetContext.bookingFlowState.completedSteps.selectContactDetails && (
        <CollapsibleStep
          step={ProvetDialogStep.ContactDetails}
          isComplete={provetContext.bookingFlowState.completedSteps.selectContactDetails}
          stepCompleteMessageText={
            provetContext?.bookingFlowState.formData?.contactDetails?.client?.firstName &&
            provetContext?.bookingFlowState.formData?.contactDetails?.client?.lastName
              ? `${provetContext.bookingFlowState.formData.contactDetails.client.firstName} ${provetContext.bookingFlowState.formData.contactDetails.client.lastName}, ${provetContext.bookingFlowState.formData.contactDetails.patient.name}`
              : undefined
          }
          isOpen={
            provetContext.bookingFlowState.activeStep === 4 // Both VerifyPhone and ContactDetails use step 4
          }
          isLoading={isPhoneVerificationLoading}
          title={
            contactDetailsSubStep === "details"
              ? translations?.dialogCustomerInformationTitle
              : contactDetailsSubStep === "verifySms"
                ? translations?.provetVerifyPhoneOtpTitle || "We sent you a code"
                : translations?.provetVerifyPhoneTitle || "Your contact information"
          }
          subtitle={
            contactDetailsSubStep === "details"
              ? translations?.dialogCustomerInformationSubtitle
              : translations?.provetVerifyPhoneSubtitle
          }
          onEdit={() => onEditStep(ProvetDialogStep.ContactDetails)}
          id={`${sessionId}_${ProvetDialogStep.ContactDetails}`}
          editButtonText={translations?.change}
          selectedValue={
            provetContext?.bookingFlowState.completedSteps.selectContactDetails
              ? `${provetContext?.bookingFlowState?.formData?.contactDetails?.client?.firstName} ${provetContext?.bookingFlowState?.formData?.contactDetails?.client?.lastName}, ${provetContext?.bookingFlowState?.formData?.contactDetails?.patient.name}`
              : undefined
          }
        >
          {/* Sub-step 1: Phone Verification */}
          {contactDetailsSubStep !== "details" && (
            <ProvetPhoneVerification
              preludeSdkKey={preludeSdkKey}
              onVerificationComplete={handlePhoneVerified}
              onStepChange={handlePhoneVerificationStepChange}
              onLoadingChange={setIsPhoneVerificationLoading}
            />
          )}

          {/* Sub-step 2: Contact Details */}
          {contactDetailsSubStep === "details" && (
            <ProvetContactDetails
              key={`contact-${formResetCounter}`}
              defaultSpecies={defaultSpecies}
              patientInfo={
                provetContext.bookingFlowState.formData?.contactDetails?.patient
              }
              onContactDetailsChanged={onContactDetailsChanged}
              onValidationError={onValidationError}
              onSpeciesSelected={handleSpeciesSelected}
            />
          )}
        </CollapsibleStep>
      )}
    </>
  );
};
